import { concludeAfterAnomalies, deriveInspectionResult } from '@ghp/domain'

import type { InspectionDraft } from '../../../offline/data-db'

/**
 * **La conclusion, réécrite chaque fois que les anomalies relevées la déplacent** — la règle du
 * prototype validé (`applyAutoConclusion`), posée sur la **voie d'écriture** du brouillon plutôt
 * que sur un écran.
 *
 * ⚠️ **C'est le seul endroit qui décide**, et c'est tout l'objet de cette fonction. La règle ne
 * vivait qu'au bout du constructeur d'anomalie ; le sélecteur de l'onglet « Contrôle », qui verse
 * dans la même liste et propose `technical/NON_RACCORDE` comme `technical/REFUS`, n'y passait pas.
 * Un agent qui concluait « Raccordé » à la main puis cochait « REFUS » transmettait « Raccordé » —
 * une donnée fausse sur une fiche que la transmission verrouille (D4). Toute voie d'écriture qui
 * s'ajoutera passera par ici sans rien à recopier.
 *
 * ⚠️ **Seul un changement de déduction rouvre la question**, et non la présence de `anomalies` ou
 * de `survey` dans le patch : préciser la provenance d'une anomalie déjà relevée réécrit la liste sans rien
 * déplacer, et rejouer la règle effacerait alors la conclusion que l'agent venait de poser.
 *
 * Ce qui survit, ce qui cède et pourquoi l'escalade rend `null` plutôt qu'une valeur : c'est
 * `concludeAfterAnomalies` (`@ghp/domain`) qui le porte, avec M19.
 */
export function withAnomalyConclusion(
  draft: InspectionDraft,
  patch: Partial<InspectionDraft>,
): Partial<InspectionDraft> {
  // Le relevé compte aussi : une case du tableau « Eaux pluviales » est une anomalie sans
  // ligne d'anomalie (`stormwaterAnomalies`), et la cocher doit déplacer la conclusion de même.
  if (patch.anomalies === undefined && patch.survey === undefined) return patch

  const anomalies = patch.anomalies ?? draft.anomalies
  const survey = patch.survey ?? draft.survey

  if (
    deriveInspectionResult(anomalies, survey) ===
    deriveInspectionResult(draft.anomalies, draft.survey)
  ) {
    return patch
  }

  return { ...patch, result: concludeAfterAnomalies({ ...draft, anomalies, survey }) }
}
